import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAccounts } from "../lib/accounts";
import { ArrowLeft, Phone, MessageSquare, Save, LogOut, Wallet, Moon, Sun, X, Sparkles, PlayCircle, Play } from "lucide-react";
import HelpButton from "../components/HelpButton";
import { getTheme, setTheme } from "../lib/theme";
import { useTour } from "../lib/tour";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const { accounts } = useAccounts();
  const tour = useTour();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [smsDefault, setSmsDefault] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [theme, setThemeState] = useState(getTheme());
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  };

  const cancelPaypal = async () => {
    if (!window.confirm(
      "Cancel your PayPal subscription?\n\n" +
      "You'll keep Pro access until the end of your current billing period, then downgrade to Free automatically."
    )) return;
    setBusy(true);
    try {
      await api.post("/paypal/subscriptions/cancel", { reason: "User cancelled from Profile" });
      toast.success("PayPal subscription cancelled");
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.phone_number) setPhone(user.phone_number);
    setSmsDefault(user?.sms_default_account_id || "");
    // If a phone is already saved, prior consent is implied — pre-check the box
    // so the user isn't re-consenting on every visit.
    setSmsConsent(!!user?.phone_number);
  }, [user]);

  const saveSmsDefault = async (aid) => {
    const next = aid === smsDefault ? "" : aid; // tapping the active chip clears
    setSmsDefault(next);
    try {
      await api.put("/auth/sms-default-account", { account_id: next || null });
      toast.success(next ? "SMS default account updated" : "SMS default cleared");
      try {
        await refreshUser();
      } catch (e) {
        console.debug("refreshUser after sms default failed:", e?.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      // roll back optimistic UI
      setSmsDefault(user?.sms_default_account_id || "");
    }
  };

  const save = async () => {
    if (phone.trim() && !smsConsent) {
      toast.error("Please check the consent box below to enable SMS.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/auth/phone", { phone_number: phone });
      toast.success(phone ? "Phone number saved" : "Phone number cleared");
      try {
        await refreshUser();
      } catch (e) {
        // Non-critical: banner will refresh on next navigation.
        console.debug("refreshUser after save phone failed:", e?.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const clearPhone = async () => {
    setPhone("");
    setBusy(true);
    try {
      await api.put("/auth/phone", { phone_number: "" });
      toast.success("Phone number cleared");
      try {
        await refreshUser();
      } catch (e) {
        // Non-critical: banner will refresh on next navigation.
        console.debug("refreshUser after clear phone failed:", e?.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Profile</h1>
        <HelpButton id="profile" />
      </div>

      {/* Account card */}
      <div className="nb-card-lg p-5 space-y-2">
        <div className="label">Account</div>
        <div className="font-display font-black text-xl">{user?.name || "You"}</div>
        <div className="text-sm font-medium text-[#4a4a4a]">{user?.email}</div>
        <div className="pt-2 flex flex-wrap items-center gap-2">
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
          >
            <LogOut size={12} strokeWidth={3} /> Log out
          </button>
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-pressed={theme === "dark"}
          >
            {theme === "dark" ? (
              <>
                <Sun size={12} strokeWidth={3} /> Light mode
              </>
            ) : (
              <>
                <Moon size={12} strokeWidth={3} /> Dark mode
              </>
            )}
          </button>
          <Link
            data-testid="merchant-rules-link"
            to="/merchant-rules"
            className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
            title="View & edit learned payee → category rules"
          >
            <Sparkles size={12} strokeWidth={3} /> Merchant Rules
          </Link>
          {user?.is_admin && (
            <Link
              data-testid="tour-videos-link"
              to="/tour-videos"
              className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
              title="Attach Loom / YouTube demos to walkthrough steps"
            >
              <Play size={12} strokeWidth={3} fill="currentColor" /> Tour videos
            </Link>
          )}
          <button
            data-testid="restart-tour-btn"
            onClick={() => tour?.start?.()}
            className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
            title="Replay the guided walkthrough from the beginning"
          >
            <PlayCircle size={12} strokeWidth={3} /> Restart walkthrough
          </button>
        </div>
      </div>

      {/* PayPal subscription card — only shows for users with an active PayPal sub.
          Backend keeps this in sync via webhooks so if PayPal cancels/suspends
          the sub externally, this card disappears on next refresh. */}
      {user?.paypal_subscription_id && (
        <div className="nb-card p-4 space-y-2" data-testid="paypal-sub-card" style={{ background: "#fdecc8" }}>
          <div className="label !mb-0">PayPal Subscription</div>
          <div className="font-display font-black text-sm">
            SyncBook Pro · {user.paypal_plan === "yearly" ? "Yearly ($29/yr)" : "Monthly ($3.99/mo)"}
          </div>
          <div className="text-[11px] font-bold text-[#4a4a4a] font-mono truncate">
            {user.paypal_subscription_id}
          </div>
          <div className="pt-1">
            <button
              data-testid="cancel-paypal-btn"
              disabled={busy}
              onClick={cancelPaypal}
              className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
              style={{ background: "#f4c2c2" }}
              title="Cancel this PayPal subscription"
            >
              <X size={12} strokeWidth={3} /> {busy ? "Cancelling…" : "Cancel subscription"}
            </button>
          </div>
        </div>
      )}

      {/* SMS setup — Pro-gated */}
      {user?.modules?.sms ? (
        <div
          className="nb-card-lg p-5 space-y-3"
          style={{ background: "#d9d4f1" }}
          data-testid="sms-setup-card"
        >
        <div className="flex items-center gap-2">
          <MessageSquare size={20} strokeWidth={3} />
          <div className="font-display font-black text-lg">Text-in transactions</div>
        </div>
        <div className="text-sm font-medium text-[#4a4a4a]">
          Link your phone to SyncBook&apos;s SMS number and you can text transactions directly into
          your register — no app needed. Great for on-the-go.
        </div>

        <div>
          <label className="label" htmlFor="phone-input">
            Your phone number
          </label>
          <input
            id="phone-input"
            data-testid="phone-input"
            type="tel"
            className="nb-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
          />
          <div className="text-[11px] font-medium text-[#4a4a4a] mt-1">
            US format works: <span className="font-mono">555-123-4567</span> or with country code{" "}
            <span className="font-mono">+15551234567</span>. Only <b>your</b> phone can text in.
          </div>
        </div>

        {/* Twilio A2P 10DLC compliant consent (unchecked by default) */}
        <label
          className="flex items-start gap-2 cursor-pointer select-none p-3 border-2 border-[#1a1a1a] rounded-xl bg-white/60"
          data-testid="sms-consent-wrap"
        >
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            data-testid="sms-consent-checkbox"
            className="w-4 h-4 mt-0.5 shrink-0"
          />
          <span className="text-[11px] font-medium leading-snug">
            I agree to receive transactional SMS from SyncBook at the number
            above. Message frequency varies (typically 1–5 msgs/day). Message
            and data rates may apply. Reply <b>STOP</b> to unsubscribe or{" "}
            <b>HELP</b> for help. See our{" "}
            <Link
              to="/privacy"
              data-testid="sms-consent-privacy"
              className="underline font-bold"
            >
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link
              to="/terms"
              data-testid="sms-consent-terms"
              className="underline font-bold"
            >
              Terms
            </Link>
            .
          </span>
        </label>

        <div className="flex gap-2">
          <button
            data-testid="save-phone-btn"
            onClick={save}
            disabled={busy || (!!phone.trim() && !smsConsent)}
            className="nb-btn nb-btn-dark flex-1 !py-2"
          >
            <Save size={14} strokeWidth={3} /> Save phone
          </button>
          {user?.phone_number && (
            <button
              data-testid="clear-phone-btn"
              onClick={clearPhone}
              disabled={busy}
              className="nb-btn nb-btn-secondary !py-2 !px-3"
              title="Unlink this phone"
            >
              Clear
            </button>
          )}
        </div>

        {accounts.length > 0 && (
          <div className="pt-3 border-t-2 border-[#1a1a1a]/20 space-y-2" data-testid="sms-default-account-picker">
            <div className="flex items-center gap-1.5">
              <Wallet size={14} strokeWidth={3} />
              <div className="label !mb-0">SMS default account</div>
            </div>
            <div className="text-[11px] font-medium text-[#4a4a4a] -mt-1">
              Where texted-in transactions land unless you add{" "}
              <span className="font-mono bg-white/60 px-1 py-0.5 rounded">acct:name</span> to the message.
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {accounts.map((a) => (
                <button
                  key={a.id}
                  data-testid={`sms-default-${a.name}`}
                  onClick={() => saveSmsDefault(a.id)}
                  className={`chip whitespace-nowrap flex items-center gap-1.5 ${smsDefault === a.id ? "active" : ""}`}
                  style={smsDefault === a.id ? { background: a.color, borderColor: "#1a1a1a" } : {}}
                  title={smsDefault === a.id ? "Tap again to clear" : `Set ${a.name} as SMS default`}
                >
                  <Wallet size={12} strokeWidth={3} />
                  {a.name}
                </button>
              ))}
            </div>
            {!smsDefault && (
              <div className="text-[11px] font-medium text-[#4a4a4a]">
                Not set — falls back to your first-created account.
              </div>
            )}
          </div>
        )}

        {user?.phone_number && (
          <div className="pt-3 border-t-2 border-[#1a1a1a]/20 space-y-2">
            <div className="flex items-center gap-1.5">
              <Phone size={14} strokeWidth={3} />
              <div className="label !mb-0">3 texts that matter</div>
            </div>
            <div className="text-[13px] font-medium space-y-2 text-[#1a1a1a]">
              <div className="grid gap-2">
                <div className="p-2 rounded-lg bg-white/70 border-2 border-[#1a1a1a]/10">
                  <div className="font-mono font-bold text-sm">-6 latte</div>
                  <div className="text-[11px] text-[#4a4a4a] font-medium mt-0.5">Logs a $6 spend and categorises it</div>
                </div>
                <div className="p-2 rounded-lg bg-white/70 border-2 border-[#1a1a1a]/10">
                  <div className="font-mono font-bold text-sm">balance</div>
                  <div className="text-[11px] text-[#4a4a4a] font-medium mt-0.5">Texts you your current balance</div>
                </div>
                <div className="p-2 rounded-lg bg-white/70 border-2 border-[#1a1a1a]/10">
                  <div className="font-mono font-bold text-sm">recent</div>
                  <div className="text-[11px] text-[#4a4a4a] font-medium mt-0.5">Sends your last 3 transactions</div>
                </div>
              </div>
              <details className="pt-1">
                <summary className="cursor-pointer text-[11px] font-bold text-[#4a4a4a] underline underline-offset-2">
                  See all commands (paychecks, natural language, acct:name, spend limits)
                </summary>
                <div className="mt-2 space-y-1 text-[12px]">
                  <div>
                    <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">+150 paycheck</span>
                    &nbsp;— deposit
                  </div>
                  <div>
                    <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">Spent 4.50 at Starbucks on food</span>
                    &nbsp;— natural language (AI-parsed)
                  </div>
                  <div>
                    Add <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">acct:main</span>{" "}
                    anywhere to pick a specific account
                  </div>
                  <div>
                    Text <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">menu</span>
                    &nbsp;— full command list via SMS
                  </div>
                </div>
              </details>
              <div className="text-[11px] italic text-[#4a4a4a]">
                💡 You'll get a text back confirming each one.
              </div>
            </div>
          </div>
        )}
      </div>
      ) : (
        <div
          className="nb-card-lg p-5 space-y-3 relative overflow-hidden"
          style={{ background: "#fdecc8" }}
          data-testid="sms-locked-card"
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={20} strokeWidth={3} />
            <div className="font-display font-black text-lg">Text-in transactions</div>
            <span className="chip !text-[10px] !py-0.5 ml-auto" style={{ background: "#1a1a1a", color: "#b5e4ca" }}>
              PRO
            </span>
          </div>
          <div className="text-sm font-medium text-[#1a1a1a]">
            Text your transactions straight into SyncBook — no app needed. Perfect for buying coffee, lunch runs, or logging a paycheck in seconds.
          </div>
          <ul className="pl-4 list-disc space-y-1 text-sm font-medium">
            <li>
              Text <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">-4.50 Starbucks food</span>
            </li>
            <li>
              Text <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">balance</span> to check your current balance
            </li>
            <li>AI-parsed natural language: &quot;Spent 4.50 at Starbucks on food&quot;</li>
          </ul>
          <Link
            to="/upgrade"
            data-testid="sms-upgrade-cta"
            className="nb-btn nb-btn-dark w-full !py-3 justify-center"
          >
            Upgrade to unlock
          </Link>
        </div>
      )}
    </div>
  );
}
